import type { ProjetoElegivel } from "@/types/finance";

const PROJETO_FINALIZADO = "encerrado";

export interface FiltrosDePeriodos {
  mostrarFinalizados: boolean;
  mostrarVinculados: boolean;
  busca: string;
}

export interface GrupoDeFreelancer {
  freelancer: ProjetoElegivel["freelancer"];
  /** Períodos que os filtros deixam na lista. */
  periodos: ProjetoElegivel[];
  finalizados: number;
  vinculados: number;
  selecionados: number;
  /** Períodos na lista que podem já ter OS no Omie. */
  comOsPossivel: number;
}

export function ehFinalizado(item: ProjetoElegivel): boolean {
  return item.status === PROJETO_FINALIZADO;
}

export function propostaConflitanteDe(item: ProjetoElegivel, excludePropostaId?: string) {
  return item.proposta_existente && item.proposta_existente.id !== excludePropostaId ? item.proposta_existente : null;
}

/** Com proposta viva, ou finalizado cuja OS já está no Omie: nunca dá para marcar. */
export function estaVinculado(item: ProjetoElegivel, excludePropostaId?: string): boolean {
  return !!propostaConflitanteDe(item, excludePropostaId) || (ehFinalizado(item) && item.tem_os_omie);
}

/** Tira o que já é da proposta atual e o finalizado de antes do histórico do Omie. */
export function periodosDisponiveis(items: ProjetoElegivel[], excludePropostaId?: string): ProjetoElegivel[] {
  return items.filter(
    (item) =>
      !(excludePropostaId && item.proposta_existente?.id === excludePropostaId) && !item.finalizado_antes_do_omie,
  );
}

function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

function atendeBusca(item: ProjetoElegivel, busca: string): boolean {
  const termo = normalizar(busca.trim());
  if (!termo) return true;
  const campos = [item.freelancer.nome, item.area_atuacao ?? "", item.vaga?.descricao_curta ?? ""];
  return normalizar(campos.join(" ")).includes(termo);
}

/**
 * Agrupa por freelancer. As contagens incluem o que está oculto, para o
 * cabeçalho dizer o que os filtros esconderam; freelancer sem nenhum período
 * na lista não aparece.
 */
export function agruparPorFreelancer(
  disponiveis: ProjetoElegivel[],
  filtros: FiltrosDePeriodos,
  selectedIds: string[],
  excludePropostaId?: string,
): GrupoDeFreelancer[] {
  const grupos = new Map<string, GrupoDeFreelancer>();
  for (const item of disponiveis) {
    if (!atendeBusca(item, filtros.busca)) continue;
    const grupo = grupos.get(item.freelancer.id) ?? {
      freelancer: item.freelancer,
      periodos: [],
      finalizados: 0,
      vinculados: 0,
      selecionados: 0,
      comOsPossivel: 0,
    };
    grupos.set(item.freelancer.id, grupo);

    const vinculado = estaVinculado(item, excludePropostaId);
    const finalizado = !vinculado && ehFinalizado(item);
    const selecionado = selectedIds.includes(item.controle_projeto_id);
    if (vinculado) grupo.vinculados += 1;
    if (finalizado) grupo.finalizados += 1;
    if (selecionado) grupo.selecionados += 1;

    const oculto = vinculado ? !filtros.mostrarVinculados : finalizado && !filtros.mostrarFinalizados && !selecionado;
    if (!oculto) {
      grupo.periodos.push(item);
      if (item.os_possiveis.length > 0) grupo.comOsPossivel += 1;
    }
  }
  return [...grupos.values()].filter((grupo) => grupo.periodos.length > 0);
}

/** Ex.: "3 na lista · 2 finalizados (visíveis) · 1 com proposta ou OS (oculto)". */
export function resumoDoGrupo(grupo: GrupoDeFreelancer, filtros: FiltrosDePeriodos): string {
  const partes = [`${grupo.periodos.length} na lista`];
  if (grupo.finalizados > 0) {
    const plural = grupo.finalizados > 1;
    const estado = filtros.mostrarFinalizados ? (plural ? "visíveis" : "visível") : plural ? "ocultos" : "oculto";
    partes.push(`${grupo.finalizados} ${plural ? "finalizados" : "finalizado"} (${estado})`);
  }
  if (grupo.vinculados > 0) {
    const plural = grupo.vinculados > 1;
    const estado = filtros.mostrarVinculados ? (plural ? "visíveis" : "visível") : plural ? "ocultos" : "oculto";
    partes.push(`${grupo.vinculados} com proposta ou OS (${estado})`);
  }
  return partes.join(" · ");
}
